import { SupportedLocale } from "@/i18n";
import { useVisibleT } from "@/lib/cms/cms-new-i18n";
import { computeFieldRelatedExhibitions } from "@/lib/computeFields";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ItinerantStopDraft } from "./ItinerantExhibitionPanel";

type ItinerantExhibitionPreviewProps = {
  stops: ItinerantStopDraft[];
};

/**
 * Live CF preview of the itinerant exhibition group (same renderer as
 * overview / frontpublic). Replaces the classic Notes field in the drawer.
 */
export function ItinerantExhibitionPreview({
  stops,
}: ItinerantExhibitionPreviewProps) {
  const { i18n } = useTranslation();
  const vt = useVisibleT();
  const locale = (i18n.language?.slice(0, 2) || "fr") as SupportedLocale;

  const html = useMemo(() => {
    if (!stops.length) return "";
    return computeFieldRelatedExhibitions(
      {
        relatedExhbitions: stops.map((stop, index) => ({
          id: stop.linkId ?? stop.id,
          exhibition: stop.exhibition,
          oeuvresDansExpositionsDescriptionNumeroCatalogue: stop.catalogNumber,
          itinerantGroupId: "preview-group",
          itinerantOrder: index,
          itinerantNote: stop.note,
        })),
        groupItinerantExhibitions: true,
      },
      { language: locale },
    );
  }, [stops, locale]);

  return (
    <div className="w-full flex flex-col gap-1.5">
      <p className="text-slate-700 text-sm font-medium font-['Raleway'] leading-normal">
        {vt("bo.itinerantExhibition.previewLabel", "Prévisualisation")}
      </p>
      <div
        className="min-h-[60px] w-full rounded-md border border-input bg-slate-50 px-3 py-2 text-sm leading-snug break-words [&_i]:text-[length:inherit] [&_em]:text-[length:inherit]"
        dangerouslySetInnerHTML={{
          __html:
            html ||
            `<span class="text-muted-foreground">${vt(
              "bo.itinerantExhibition.previewEmpty",
              "Ajoutez des étapes pour prévisualiser le rendu.",
            )}</span>`,
        }}
      />
    </div>
  );
}
